import React, { useEffect, useRef, useState } from 'react';
import { Calendar, MapPin, Briefcase } from 'lucide-react';

const Experience = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const experiences = [
    {
      title: 'AI Developer & Ambassador',
      company: 'CAMEL-AI',
      location: 'Remote',
      period: 'Apr 2024 - Present',
      description: [
        'Developed 20+ multi-agent systems for real-world automation use cases',
        'Designed RAG pipelines with vector search, improving retrieval accuracy by 35%',
        'Contributed to open-source efforts and shared technical content on agent architectures'
      ],
      impact: ['20+ systems delivered', '35% retrieval improvement'],
      stack: ['CAMEL-AI', 'Python', 'RAG', 'LLMs']
    },
    {
      title: 'Freelance AI Developer',
      company: 'Independent',
      location: 'Remote',
      period: '2024 - Present',
      description: [
        'Delivered end-to-end AI applications for client requirements',
        'Built scalable products using modern Python frameworks and APIs',
        'Managed complete solution delivery from design to deployment'
      ],
      impact: ['Client-focused delivery', 'Production-ready solutions'],
      stack: ['Python', 'FastAPI', 'APIs']
    }
  ];

  const stats = [
    { label: 'Roles', value: `${experiences.length}+` },
    { label: 'Agent Systems', value: '20+' },
    { label: 'RAG Impact', value: '35%' }
  ];

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="py-16 px-4 md:px-6"
      style={{
        background: 'var(--bg-secondary)',
        minHeight: 'auto'
      }}
    >
      <style>{`
        .fade-in {
          animation: fadeInUp 0.6s ease-out forwards;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #00d4ff, #a855f7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .exp-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }
        
        .exp-card:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(0, 212, 255, 0.2);
          transform: translateY(-2px);
        }
        
        .timeline-line {
          background: linear-gradient(180deg, #00d4ff, #a855f7, transparent);
        }
        
        .timeline-dot {
          box-shadow: 0 0 12px rgba(0, 212, 255, 0.5);
        }
      `}</style>

      <div className="max-w-5xl mx-auto w-full">
        {/* Section Title */}
        <div className={`text-center mb-12 ${isVisible ? 'fade-in' : 'opacity-0'}`}>
          <h2
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }}
          >
            Work <span className="gradient-text">Experience</span>
          </h2>
          <div
            className="w-16 h-0.5 mx-auto rounded-full"
            style={{ background: 'var(--brand-primary)' }}
          />
          <p
            className="mt-4 max-w-2xl mx-auto text-sm md:text-base"
            style={{ color: 'var(--text-secondary)' }}
          >
            Hands-on experience building AI products from concept to production,
            combining reliable engineering with practical business impact.
          </p>
        </div>

        {/* Highlight Stats */}
        <div className="grid grid-cols-3 gap-3 mb-10">
          {stats.map((item, index) => (
            <div
              key={item.label}
              className={`rounded-xl px-3 py-4 text-center ${isVisible ? 'fade-in' : 'opacity-0'}`}
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                animationDelay: `${index * 0.1}s`
              }}
            >
              <p className="text-lg md:text-xl font-bold" style={{ color: 'var(--brand-primary)' }}>
                {item.value}
              </p>
              <p className="text-xs md:text-sm" style={{ color: 'var(--text-muted)' }}>
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div
            className="hidden md:block absolute left-6 top-0 h-full w-0.5 timeline-line"
          />

          {/* Experience Cards */}
          <div className="space-y-6">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className={`relative ${isVisible ? 'fade-in' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="md:pl-16">
                  {/* Timeline Dot */}
                  <div
                    className="hidden md:flex absolute left-4 top-6 w-5 h-5 rounded-full timeline-dot items-center justify-center"
                    style={{
                      background: 'var(--brand-primary)',
                      border: '3px solid var(--bg-primary)'
                    }}
                  />

                  <div className="exp-card rounded-xl p-5 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(168, 85, 247, 0.15))',
                            border: '1px solid rgba(0, 212, 255, 0.2)'
                          }}
                        >
                          <Briefcase size={18} style={{ color: 'var(--brand-primary)' }} />
                        </div>

                        <div>
                          {/* Title */}
                          <h3
                            className="text-lg font-semibold"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {exp.title}
                          </h3>

                          {/* Company */}
                          <p
                            className="text-sm font-medium"
                            style={{ color: 'var(--brand-primary)' }}
                          >
                            {exp.company}
                          </p>
                        </div>
                      </div>

                      {/* Meta Info */}
                      <div className="flex items-center gap-4 md:gap-3 text-xs md:text-sm flex-shrink-0">
                        <div className="flex items-center gap-1.5">
                          <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>
                            {exp.period}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
                          <span style={{ color: 'var(--text-muted)' }}>
                            {exp.location}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Impact */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {exp.impact.map((pill) => (
                        <span
                          key={pill}
                          className="text-xs px-2.5 py-1 rounded-full"
                          style={{
                            background: 'rgba(0, 212, 255, 0.12)',
                            border: '1px solid rgba(0, 212, 255, 0.3)',
                            color: 'var(--brand-primary)'
                          }}
                        >
                          {pill}
                        </span>
                      ))}
                    </div>

                    {/* Description */}
                    <ul className="space-y-1.5 ml-0 md:ml-12">
                      {exp.description.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm"
                          style={{
                            color: 'var(--text-secondary)',
                            lineHeight: '1.5'
                          }}
                        >
                          <span 
                            className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" 
                            style={{ background: 'var(--brand-primary)' }}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Core Stack */}
                    <div className="mt-4 flex flex-wrap gap-2 md:ml-12">
                      {exp.stack.map((tech) => (
                        <span
                          key={tech}
                          className="text-xs px-2 py-1 rounded-md"
                          style={{
                            background: 'rgba(255, 255, 255, 0.04)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;